import { useEffect, useMemo, useState } from "react";

import {
  applyFleetOrderEmergencyAmendment,
  getFleetOrderAmendmentWorkspace,
  previewFleetOrderEmergencyAmendment,
} from "../../api/client.js";
import { Dialog } from "../Dialog.jsx";
import { Icon } from "../Icon.jsx";
import { RouteBadge } from "../RouteBadge.jsx";
import { formatVolume, formatWeight } from "../../utils/number-format.js";

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function impactValue(value, formatter = (current) => current) {
  if (value === null || value === undefined) return "—";
  return formatter(value);
}

function CapacityState({ capacity }) {
  if (!capacity) return null;
  return (
    <div className="fleet-amendment-capacity">
      <div>
        <span>Berat setelah perubahan</span>
        <strong>{formatWeight(capacity.afterWeightKg)}</strong>
        <small>
          Kapasitas {capacity.capacityKg ? formatWeight(capacity.capacityKg) : "belum ditetapkan"}
        </small>
      </div>
      <div>
        <span>Volume setelah perubahan</span>
        <strong>{formatVolume(capacity.afterVolumeM3)}</strong>
        <small>
          Kapasitas {capacity.capacityM3 ? formatVolume(capacity.capacityM3) : "belum ditetapkan"}
        </small>
      </div>
      <span className={`fleet-amendment-capacity__status ${capacity.safe ? "is-safe" : "is-blocked"}`}>
        {capacity.safe ? "Kapasitas aman" : "Kapasitas terlampaui"}
      </span>
    </div>
  );
}

export function FleetOrderEmergencyAmendmentDialog({ fleetOrder, onApplied, onClose }) {
  const [workspace, setWorkspace] = useState(null);
  const [search, setSearch] = useState("");
  const [remoteCandidates, setRemoteCandidates] = useState(null);
  const [searchingCandidates, setSearchingCandidates] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reason, setReason] = useState("");
  const [vehicleStillAtDepotConfirmed, setVehicleStillAtDepotConfirmed] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getFleetOrderAmendmentWorkspace(fleetOrder.id, { limit: 200 })
      .then((payload) => {
        if (active) setWorkspace(payload);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fleetOrder.id]);

  useEffect(() => {
    const normalizedSearch = search.trim();
    if (!normalizedSearch) {
      setRemoteCandidates(null);
      setSearchingCandidates(false);
      return undefined;
    }

    let active = true;
    const timeoutId = window.setTimeout(() => {
      setSearchingCandidates(true);
      getFleetOrderAmendmentWorkspace(fleetOrder.id, {
        search: normalizedSearch,
        limit: 100,
      })
        .then((payload) => {
          if (active) setRemoteCandidates(safeArray(payload?.candidates));
        })
        .catch((requestError) => {
          if (active) setError(requestError.message);
        })
        .finally(() => {
          if (active) setSearchingCandidates(false);
        });
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [fleetOrder.id, search]);

  const candidates = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const all = normalizedSearch
      ? safeArray(remoteCandidates)
      : safeArray(workspace?.candidates);
    if (!normalizedSearch) return all;
    return all.filter((order) =>
      [order.soNumber, order.customerName, order.resolvedArea, order.resolvedRouteName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch)),
    );
  }, [workspace, remoteCandidates, search]);

  const policy = workspace?.operationPolicy || fleetOrder.operationPolicy || {};
  const reasonValid = reason.trim().length >= 10;
  const depotConfirmationValid =
    !policy.requiresStillAtDepotConfirmation || vehicleStillAtDepotConfirmed;
  const canPreview = selectedIds.length > 0 && reasonValid && depotConfirmationValid;

  function toggleOrder(id) {
    setPreview(null);
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );
  }

  async function loadPreview() {
    setPreviewing(true);
    setError("");
    try {
      const payload = await previewFleetOrderEmergencyAmendment(fleetOrder.id, {
        salesOrderIds: selectedIds,
        reason: reason.trim(),
        vehicleStillAtDepotConfirmed,
        createdBy: "Operasional",
      });
      setPreview(payload);
    } catch (requestError) {
      setError(requestError.message);
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }

  async function applyAmendment() {
    setApplying(true);
    setError("");
    try {
      const payload = await applyFleetOrderEmergencyAmendment(fleetOrder.id, {
        salesOrderIds: selectedIds,
        reason: reason.trim(),
        vehicleStillAtDepotConfirmed,
        createdBy: "Operasional",
      });
      onApplied(payload);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setApplying(false);
    }
  }

  return (
    <Dialog
      className="dialog-card--fleet-amendment"
      onClose={() => !applying && onClose()}
      title="Tambahkan Pesanan Darurat"
    >
      <div className="dialog-card__body fleet-amendment-dialog__content">
        <div className="fleet-amendment-context">
          <span className="fleet-amendment-context__icon"><Icon name="warning" size={20} /></span>
          <div>
            <strong>{fleetOrder.foNumber}</strong>
            <span>
              {fleetOrder.foRouteName || fleetOrder.routeName || "Rute belum tersedia"} · {fleetOrder.depot?.depotName || fleetOrder.depotName || "Depot belum tersedia"}
            </span>
          </div>
          <span className="fleet-amendment-context__phase">
            {policy.operationalPhase === "IN_PROGRESS" ? "Perjalanan dibuka di depot" : "Persiapan di depot"}
          </span>
        </div>

        <div className="fleet-amendment-safety-note" role="note">
          <Icon name="info" size={18} />
          <div>
            <strong>Perubahan dibatasi sebelum titik pengiriman dimulai.</strong>
            <span>
              Pesanan tambahan akan membuka kembali loading, membuat rute perlu ditinjau, dan wajib dikonfirmasi driver.
            </span>
          </div>
        </div>

        {error && <div className="error-banner" role="alert">{error}</div>}

        {loading ? (
          <div className="fleet-amendment-state">Memuat Sales Order yang tersedia…</div>
        ) : !policy.canEmergencyAmend ? (
          <div className="fleet-amendment-state is-blocked">
            <Icon name="warning" size={22} />
            <strong>Perubahan darurat tidak tersedia</strong>
            <span>{policy.emergencyAmendmentReason || "Tahap Fleet Order saat ini sudah terkunci."}</span>
          </div>
        ) : (
          <div className="fleet-amendment-layout">
            <section className="fleet-amendment-selection" aria-labelledby="fleet-amendment-orders-title">
              <div className="fleet-amendment-section-heading">
                <div>
                  <span>Langkah 1</span>
                  <h3 id="fleet-amendment-orders-title">Pilih Sales Order tambahan</h3>
                </div>
                <strong>{selectedIds.length} dipilih</strong>
              </div>

              <label className="fleet-amendment-search">
                <Icon name="search" size={17} />
                <input
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Cari nomor SO, pelanggan, area, atau rute"
                  type="search"
                  value={search}
                />
              </label>

              <div className="fleet-amendment-candidate-help">
                <Icon name="info" size={14} />
                <span>
                  SO pending maupun terproses dapat dipilih selama tidak terhubung ke Fleet Order aktif.
                  Relasi FO lama yang sudah dilepas tidak menghalangi penjadwalan ulang.
                </span>
              </div>

              <div className="fleet-amendment-order-list">
                {searchingCandidates ? (
                  <div className="fleet-amendment-empty">Mencari Sales Order…</div>
                ) : candidates.length ? candidates.map((order) => {
                  const selected = selectedIds.includes(order.id);
                  return (
                    <label className={`fleet-amendment-order ${selected ? "is-selected" : ""}`} key={order.id}>
                      <input
                        checked={selected}
                        onChange={() => toggleOrder(order.id)}
                        type="checkbox"
                      />
                      <span className="fleet-amendment-order__main">
                        <strong>{order.soNumber}</strong>
                        <span>{order.customerName}</span>
                        <small>
                          {formatDate(order.orderDate)} · {order.resolvedArea || "Tanpa area"}
                          {order.processingStatusLabel ? ` · ${order.processingStatusLabel}` : ""}
                        </small>
                      </span>
                      <span className="fleet-amendment-order__meta">
                        <RouteBadge name={order.resolvedRouteName || order.resolvedRouteCode} />
                        <small>{formatWeight(order.totalWeightKg || 0)}</small>
                        <small>{formatVolume(order.totalVolumeM3 || 0)}</small>
                      </span>
                    </label>
                  );
                }) : (
                  <div className="fleet-amendment-empty">
                    Tidak ada Sales Order yang memenuhi syarat.
                  </div>
                )}
              </div>
            </section>

            <section className="fleet-amendment-review" aria-labelledby="fleet-amendment-review-title">
              <div className="fleet-amendment-section-heading">
                <div>
                  <span>Langkah 2</span>
                  <h3 id="fleet-amendment-review-title">Alasan dan dampak perubahan</h3>
                </div>
              </div>

              <label className="fleet-amendment-reason">
                <span>Alasan perubahan darurat</span>
                <textarea
                  maxLength={500}
                  onChange={(event) => {
                    setReason(event.target.value);
                    setPreview(null);
                  }}
                  placeholder="Contoh: Pesanan urgent harus dikirim hari ini dan kendaraan masih berada di depot."
                  rows={4}
                  value={reason}
                />
                <small>{reason.trim().length}/500 · minimal 10 karakter</small>
              </label>

              {policy.requiresStillAtDepotConfirmation && (
                <label className="fleet-amendment-depot-confirmation">
                  <input
                    checked={vehicleStillAtDepotConfirmed}
                    onChange={(event) => {
                      setVehicleStillAtDepotConfirmed(event.target.checked);
                      setPreview(null);
                    }}
                    type="checkbox"
                  />
                  <span>
                    <strong>Saya memastikan kendaraan masih berada di depot.</strong>
                    <small>Perjalanan sudah dibuka. Konfirmasi operasional wajib karena lokasi mulai perjalanan tidak membuktikan posisi kendaraan saat ini.</small>
                  </span>
                </label>
              )}

              {!preview ? (
                <div className="fleet-amendment-preview-placeholder">
                  <Icon name="database" size={24} />
                  <strong>Tinjau dampak sebelum menerapkan</strong>
                  <span>Sistem akan memeriksa kapasitas, status SO, dan data logistik terbaru.</span>
                  <button
                    className="secondary-button"
                    disabled={!canPreview || previewing}
                    onClick={loadPreview}
                    type="button"
                  >
                    {previewing ? "Memeriksa…" : "Tinjau Dampak"}
                  </button>
                </div>
              ) : (
                <div className="fleet-amendment-preview">
                  <div className="fleet-amendment-impact-grid">
                    <div>
                      <span>Dokumen</span>
                      <strong>
                        {preview.impact.before.documentCount} → {preview.impact.after.documentCount}
                      </strong>
                      <small>+{preview.impact.added.documentCount} Sales Order</small>
                    </div>
                    <div>
                      <span>Stop</span>
                      <strong>
                        {preview.impact.before.stopCount} → {preview.impact.after.estimatedStopCount}
                      </strong>
                      <small>Estimasi +{preview.impact.added.estimatedStopCount} stop</small>
                    </div>
                    <div>
                      <span>Berat tambahan</span>
                      <strong>{impactValue(preview.impact.added.weightKg, formatWeight)}</strong>
                    </div>
                    <div>
                      <span>Volume tambahan</span>
                      <strong>{impactValue(preview.impact.added.volumeM3, formatVolume)}</strong>
                    </div>
                  </div>

                  <CapacityState capacity={preview.impact.capacity} />

                  {safeArray(preview.warnings).length > 0 && (
                    <div className="fleet-amendment-warning-list">
                      <strong>Perlu diperhatikan</strong>
                      {preview.warnings.map((warning) => (
                        <span key={warning}><Icon name="warning" size={15} /> {warning}</span>
                      ))}
                    </div>
                  )}

                  <div className="fleet-amendment-next-steps">
                    <strong>Setelah diterapkan</strong>
                    <span>1. Driver menerima notifikasi perubahan.</span>
                    <span>2. Driver mengonfirmasi perubahan diterima.</span>
                    <span>3. Loading stop terdampak wajib diperiksa ulang.</span>
                    <span>4. Titik pengiriman tetap terkunci sampai loading dikonfirmasi.</span>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      <footer className="dialog-card__actions fleet-amendment-dialog__actions">
        <button className="secondary-button" disabled={applying} onClick={onClose} type="button">
          Batal
        </button>
        <button
          className="primary-button fleet-amendment-submit"
          disabled={!preview?.canApply || applying}
          onClick={applyAmendment}
          type="button"
        >
          {applying ? "Menerapkan…" : "Terapkan Perubahan Darurat"}
        </button>
      </footer>
    </Dialog>
  );
}
